import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';
import { format } from 'date-fns';

function SkeletonBlock({ className }) {
  return <div className={`bg-[var(--color-muted)] animate-pulse rounded ${className}`} />;
}

export default function MeasurementTracking({ memberId }) {
  const [measurements, setMeasurements] = useState([]);
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unit, setUnit] = useState('kg');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({
    weight_kg: '', body_fat_pct: '', chest_cm: '', waist_cm: '',
    hips_cm: '', bicep_cm: '', thigh_cm: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      setMeasurements([]);
      setGoal(null);
    } catch (e) {
      setError(e?.message ?? 'Failed to load measurements');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toDisplay = (kg) => {
    if (!kg) return '—';
    if (unit === 'lbs') return `${(kg * 2.20462)?.toFixed(1)} lbs`;
    return `${kg} kg`;
  };

  const chartData = measurements?.map(m => ({
    date: format(new Date(m?.recorded_at), 'MMM d'),
    weight: unit === 'lbs' ? parseFloat((m?.weight_kg * 2.20462)?.toFixed(1)) : m?.weight_kg,
    bodyFat: m?.body_fat_pct,
  }));

  const targetWeight = goal?.target_weight
    ? (unit === 'lbs' ? parseFloat((goal?.target_weight * 2.20462)?.toFixed(1)) : goal?.target_weight)
    : null;

  const first = measurements?.[0];
  const last = measurements?.[measurements?.length - 1];

  const delta = (field) => {
    if (!first || !last || first?.[field] == null || last?.[field] == null) return null;
    return parseFloat((last?.[field] - first?.[field])?.toFixed(1));
  };

  const weightDelta = delta('weight_kg');
  const fatDelta = delta('body_fat_pct');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!memberId) return;
    setSubmitting(true);
    setFormError(null);
    try {
      throw new Error('Measurement logging is not available in this version');
      setShowForm(false);
      setForm({ weight_kg: '', body_fat_pct: '', chest_cm: '', waist_cm: '', hips_cm: '', bicep_cm: '', thigh_cm: '', notes: '' });
      fetchData();
    } catch (e) {
      setFormError(e?.message ?? 'Failed to save measurement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        {[1, 2, 3]?.map(i => <SkeletonBlock key={i} className="h-48" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-[var(--radius-md)]">
        <Icon name="AlertCircle" size={16} color="var(--color-error)" />
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Delta Badges */}
          {weightDelta !== null && (
            <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium font-[var(--font-caption)] ${
              weightDelta < 0 ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' : 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
            }`}>
              <Icon name={weightDelta < 0 ? 'TrendingDown' : 'TrendingUp'} size={14} color="currentColor" />
              {weightDelta > 0 ? '+' : ''}{unit === 'lbs' ? (weightDelta * 2.20462)?.toFixed(1) : weightDelta} {unit} since start
            </span>
          )}
          {fatDelta !== null && (
            <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium font-[var(--font-caption)] ${
              fatDelta < 0 ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' : 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
            }`}>
              <Icon name={fatDelta < 0 ? 'TrendingDown' : 'TrendingUp'} size={14} color="currentColor" />
              {fatDelta > 0 ? '+' : ''}{fatDelta}% body fat
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Unit Toggle */}
          <div className="flex bg-[var(--color-muted)] rounded-[var(--radius-sm)] p-1">
            {['kg', 'lbs']?.map(u => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                className={`px-3 py-1 rounded text-xs font-medium font-[var(--font-caption)] transition-all ${
                  unit === u ? 'bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm' : 'text-[var(--color-muted-foreground)]'
                }`}
              >
                {u}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
          >
            <Icon name="Plus" size={14} color="#fff" />
            Log Measurement
          </button>
        </div>
      </div>
      {/* Log Form */}
      {showForm && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Log New Measurement</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {[
                { key: 'weight_kg', label: 'Weight (kg)', placeholder: '80.5' },
                { key: 'body_fat_pct', label: 'Body Fat (%)', placeholder: '20.0' },
                { key: 'chest_cm', label: 'Chest (cm)', placeholder: '100' },
                { key: 'waist_cm', label: 'Waist (cm)', placeholder: '85' },
                { key: 'hips_cm', label: 'Hips (cm)', placeholder: '95' },
                { key: 'bicep_cm', label: 'Bicep (cm)', placeholder: '36' },
                { key: 'thigh_cm', label: 'Thigh (cm)', placeholder: '55' },
              ]?.map(f => (
                <div key={f?.key}>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">{f?.label}</label>
                  <input
                    type="number" step="0.1" placeholder={f?.placeholder}
                    value={form?.[f?.key]}
                    onChange={e => setForm(prev => ({ ...prev, [f?.key]: e?.target?.value }))}
                    className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                  />
                </div>
              ))}
              <div className="col-span-2 md:col-span-4">
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Notes (optional)</label>
                <input
                  type="text" placeholder="Any notes..."
                  value={form?.notes}
                  onChange={e => setForm(prev => ({ ...prev, notes: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                />
              </div>
            </div>
            {formError && (
              <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)] mb-3">{formError}</p>
            )}
            <div className="flex gap-3">
              <button
                type="submit" disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={14} color="#fff" />}
                {submitting ? 'Saving...' : 'Save Measurement'}
              </button>
              <button
                type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] font-[var(--font-caption)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      {measurements?.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-20 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)]">
          <Icon name="Ruler" size={32} color="var(--color-muted-foreground)" />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">No measurements logged yet</h3>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Click "Log Measurement" to start tracking your body metrics.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Weight Chart */}
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
            <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Weight Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }} />
                <Tooltip
                  contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-caption)' }}
                />
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12, fontFamily: 'var(--font-caption)' }} />
                <Line type="monotone" dataKey="weight" name={`Weight (${unit})`} stroke="var(--color-primary)" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                {targetWeight && (
                  <ReferenceLine y={targetWeight} stroke="var(--color-accent)" strokeDasharray="6 3" label={{ value: `Target: ${targetWeight}${unit}`, fill: 'var(--color-accent)', fontSize: 11, fontFamily: 'var(--font-caption)' }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Measurement History Table */}
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
            <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Measurement History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-[var(--font-caption)]">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    {['Date', `Weight (${unit})`, 'Body Fat %', 'Chest', 'Waist', 'Hips', 'Bicep', 'Thigh']?.map(h => (
                      <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-[var(--color-muted-foreground)] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...measurements]?.reverse()?.map((m, idx) => (
                    <tr key={m?.id} className={`border-b border-[var(--color-border)]/50 hover:bg-[var(--color-muted)]/30 transition-colors ${idx === 0 ? 'bg-[var(--color-primary)]/3' : ''}`}>
                      <td className="py-2.5 px-3 text-[var(--color-foreground)] whitespace-nowrap">{format(new Date(m?.recorded_at), 'MMM d, yyyy')}</td>
                      <td className="py-2.5 px-3 font-medium text-[var(--color-foreground)]">{toDisplay(m?.weight_kg)}</td>
                      <td className="py-2.5 px-3 text-[var(--color-foreground)]">{m?.body_fat_pct ? `${m?.body_fat_pct}%` : '—'}</td>
                      <td className="py-2.5 px-3 text-[var(--color-foreground)]">{m?.chest_cm ? `${m?.chest_cm}cm` : '—'}</td>
                      <td className="py-2.5 px-3 text-[var(--color-foreground)]">{m?.waist_cm ? `${m?.waist_cm}cm` : '—'}</td>
                      <td className="py-2.5 px-3 text-[var(--color-foreground)]">{m?.hips_cm ? `${m?.hips_cm}cm` : '—'}</td>
                      <td className="py-2.5 px-3 text-[var(--color-foreground)]">{m?.bicep_cm ? `${m?.bicep_cm}cm` : '—'}</td>
                      <td className="py-2.5 px-3 text-[var(--color-foreground)]">{m?.thigh_cm ? `${m?.thigh_cm}cm` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
